<div class="p-6 lg:p-8">

  <!-- ── Page header ──────────────────────────────────────────────────────── -->
  <div class="mb-8 flex items-center justify-between">
    <div>
      <h1 class="text-xl font-semibold text-slate-900">Overview</h1>
      <p class="mt-0.5 text-sm text-slate-500" *ngIf="lastRefreshedAt">
        Last refreshed {{ relativeTime(lastRefreshedAt) }}
      </p>
    </div>
    <button
      (click)="load()"
      [disabled]="loading"
      class="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
    >
      <lucide-icon name="refresh-cw" [size]="14" [class.animate-spin]="loading" [strokeWidth]="2"></lucide-icon>
      Refresh
    </button>
  </div>

  <!-- ── Loading skeleton ──────────────────────────────────────────────────── -->
  <div *ngIf="loading" class="space-y-6">
    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <div *ngFor="let i of [0,1,2,3]" class="h-28 animate-pulse rounded-xl border border-slate-200 bg-slate-100"></div>
    </div>
    <div class="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <div class="h-64 animate-pulse rounded-xl border border-slate-200 bg-slate-100 lg:col-span-2"></div>
      <div class="h-64 animate-pulse rounded-xl border border-slate-200 bg-slate-100"></div>
    </div>
    <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div class="h-48 animate-pulse rounded-xl border border-slate-200 bg-slate-100"></div>
      <div class="h-48 animate-pulse rounded-xl border border-slate-200 bg-slate-100"></div>
    </div>
  </div>

  <!-- ── Error state ────────────────────────────────────────────────────────── -->
  <div *ngIf="!loading && error" class="flex flex-col items-center justify-center py-24 text-center">
    <div class="flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50">
      <lucide-icon name="circle-x" [size]="26" class="text-red-400" [strokeWidth]="1.5"></lucide-icon>
    </div>
    <h3 class="mt-4 text-sm font-semibold text-slate-900">Failed to load overview</h3>
    <p class="mt-1 text-sm text-slate-500">Check your connection and try again.</p>
    <button
      (click)="load()"
      class="mt-5 rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700"
    >Retry</button>
  </div>

  <!-- ── Main content ───────────────────────────────────────────────────────── -->
  <div *ngIf="!loading && !error" class="space-y-6">

    <!-- ══ SECTION 1: KPI Row ═══════════════════════════════════════════════ -->
    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">

      <!-- Open Findings -->
      <div class="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div class="flex items-start justify-between">
          <p class="text-xs font-medium uppercase tracking-wide text-slate-400">Open Findings</p>
          <div class="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100">
            <lucide-icon name="activity" [size]="18" class="text-slate-500" [strokeWidth]="1.75"></lucide-icon>
          </div>
        </div>
        <p class="mt-2 text-3xl font-semibold tracking-tight text-slate-900">{{ openCount }}</p>
        <p class="mt-1.5 text-xs text-slate-400">across all monitored services</p>
      </div>

      <!-- Critical Findings -->
      <div
        class="rounded-xl border p-5 shadow-sm"
        [class.border-red-200]="criticalCount > 0"
        [class.bg-red-50]="criticalCount > 0"
        [class.border-slate-200]="criticalCount === 0"
        [class.bg-white]="criticalCount === 0"
      >
        <div class="flex items-start justify-between">
          <p
            class="text-xs font-medium uppercase tracking-wide"
            [class.text-red-500]="criticalCount > 0"
            [class.text-slate-400]="criticalCount === 0"
          >Critical</p>
          <div
            class="flex h-9 w-9 items-center justify-center rounded-lg"
            [class.bg-red-100]="criticalCount > 0"
            [class.bg-slate-100]="criticalCount === 0"
          >
            <lucide-icon
              name="triangle-alert"
              [size]="18"
              [class.text-red-500]="criticalCount > 0"
              [class.text-slate-400]="criticalCount === 0"
              [strokeWidth]="1.75"
            ></lucide-icon>
          </div>
        </div>
        <p
          class="mt-2 text-3xl font-semibold tracking-tight"
          [class.text-red-700]="criticalCount > 0"
          [class.text-slate-900]="criticalCount === 0"
        >{{ criticalCount }}</p>
        <p
          class="mt-1.5 text-xs"
          [class.text-red-400]="criticalCount > 0"
          [class.text-slate-400]="criticalCount === 0"
        >{{ criticalCount > 0 ? 'need immediate action' : 'no critical findings' }}</p>
      </div>

      <!-- Median Age -->
      <div class="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div class="flex items-start justify-between">
          <p class="text-xs font-medium uppercase tracking-wide text-slate-400">Median Age</p>
          <div class="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100">
            <lucide-icon name="clock" [size]="18" class="text-slate-500" [strokeWidth]="1.75"></lucide-icon>
          </div>
        </div>
        <p class="mt-2 text-3xl font-semibold tracking-tight text-slate-900">
          {{ medianAgeDays !== null ? formatMedianAge(medianAgeDays) : '—' }}
        </p>
        <p class="mt-1.5 text-xs text-slate-400">of open findings</p>
      </div>

      <!-- Resolved This Week -->
      <div class="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div class="flex items-start justify-between">
          <p class="text-xs font-medium uppercase tracking-wide text-slate-400">Resolved This Week</p>
          <div class="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50">
            <lucide-icon name="circle-check" [size]="18" class="text-emerald-500" [strokeWidth]="1.75"></lucide-icon>
          </div>
        </div>
        <p class="mt-2 text-3xl font-semibold tracking-tight text-slate-900">{{ resolvedThisWeek }}</p>
        <p class="mt-1.5 text-xs text-slate-400">in the last 7 days</p>
      </div>

    </div>

    <!-- ══ SECTION 2 + coverage: Action Required + Ticket Coverage ══════════ -->
    <div class="grid grid-cols-1 gap-6 lg:grid-cols-3">

      <!-- Action Required (2/3) -->
      <div class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm lg:col-span-2">
        <div class="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <div>
            <h2 class="text-sm font-semibold text-slate-900">Action Required</h2>
            <p class="text-xs text-slate-400">Open findings, sorted by severity and age</p>
          </div>
          <span *ngIf="openCount > 0" class="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">
            {{ openCount }} open
          </span>
        </div>

        <!-- Empty: all clear -->
        <div *ngIf="actionRequired.length === 0" class="flex flex-col items-center justify-center py-12 text-center">
          <div class="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-50">
            <lucide-icon name="circle-check" [size]="22" class="text-emerald-500" [strokeWidth]="1.75"></lucide-icon>
          </div>
          <p class="mt-3 text-sm font-medium text-slate-700">All clear</p>
          <p class="mt-0.5 text-xs text-slate-400">No open findings right now.</p>
        </div>

        <!-- Findings table -->
        <div *ngIf="actionRequired.length > 0" class="overflow-x-auto">
          <table class="w-full">
            <thead>
              <tr class="border-b border-slate-100">
                <th class="px-5 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-400">Severity</th>
                <th class="px-5 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-400">Finding</th>
                <th class="px-5 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-400">Service</th>
                <th class="px-5 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-400">Age</th>
                <th class="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-50">
              <tr *ngFor="let f of actionRequired" class="transition-colors hover:bg-slate-50">
                <td class="px-5 py-3.5">
                  <span
                    class="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium"
                    [ngClass]="severityBadgeClass(f.severity)"
                  >
                    <span class="h-1.5 w-1.5 rounded-full" [ngClass]="severityDotClass(f.severity)"></span>
                    {{ f.severity | titlecase }}
                  </span>
                </td>
                <td class="max-w-xs px-5 py-3.5">
                  <p class="truncate text-sm font-medium text-slate-900">{{ f.title }}</p>
                  <p class="truncate text-xs text-slate-400">{{ f.resourceId }}</p>
                </td>
                <td class="px-5 py-3.5">
                  <span class="text-xs text-slate-500">{{ serviceTypeLabel(f.serviceType) }}</span>
                </td>
                <td class="px-5 py-3.5 text-xs text-slate-500 whitespace-nowrap">{{ formatAge(f.observedAt) }}</td>
                <td class="px-5 py-3.5">
                  <a
                    [routerLink]="serviceRoute(f.serviceType)"
                    class="flex items-center gap-0.5 text-xs font-medium text-violet-600 hover:text-violet-800"
                  >
                    View
                    <lucide-icon name="arrow-right" [size]="12" [strokeWidth]="2.5"></lucide-icon>
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Ticket Coverage (1/3) -->
      <div class="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div class="border-b border-slate-100 px-5 py-4">
          <h2 class="text-sm font-semibold text-slate-900">Ticket Coverage</h2>
          <p class="text-xs text-slate-400">Open resources with linked tickets</p>
        </div>
        <div class="p-5">

          <!-- Coverage percentage -->
          <div class="mb-4 flex items-end justify-between">
            <span class="text-3xl font-semibold tracking-tight text-slate-900">{{ coveragePercent }}%</span>
            <span class="text-xs text-slate-400">{{ ticketedCount }} of {{ openResourceCount }}</span>
          </div>

          <!-- Progress bar -->
          <div class="mb-5 h-2 w-full overflow-hidden rounded-full bg-slate-100">
            <div
              class="h-full rounded-full transition-all duration-500"
              [class.bg-emerald-500]="coveragePercent === 100"
              [class.bg-violet-500]="coveragePercent >= 50 && coveragePercent < 100"
              [class.bg-amber-400]="coveragePercent > 0 && coveragePercent < 50"
              [class.bg-red-400]="coveragePercent === 0 && openResourceCount > 0"
              [class.bg-slate-200]="openResourceCount === 0"
              [style.width.%]="coveragePercent"
            ></div>
          </div>

          <!-- Breakdown by provider -->
          <div class="space-y-3">
            <a routerLink="/github" class="flex items-center justify-between rounded-lg bg-slate-50 px-3.5 py-2.5 hover:bg-slate-100 transition-colors">
              <div class="flex items-center gap-2.5">
                <div class="flex h-7 w-7 items-center justify-center rounded-md bg-slate-800">
                  <lucide-icon name="github" [size]="14" class="text-white" [strokeWidth]="1.75"></lucide-icon>
                </div>
                <span class="text-sm font-medium text-slate-700">GitHub Issues</span>
              </div>
              <span class="text-sm font-semibold text-slate-900">{{ ghTicketCount }}</span>
            </a>
            <a routerLink="/jira" class="flex items-center justify-between rounded-lg bg-slate-50 px-3.5 py-2.5 hover:bg-slate-100 transition-colors">
              <div class="flex items-center gap-2.5">
                <div class="flex h-7 w-7 items-center justify-center rounded-md bg-blue-600">
                  <lucide-icon name="list-todo" [size]="14" class="text-white" [strokeWidth]="1.75"></lucide-icon>
                </div>
                <span class="text-sm font-medium text-slate-700">Jira Tickets</span>
              </div>
              <span class="text-sm font-semibold text-slate-900">{{ jiraTicketCount }}</span>
            </a>
          </div>

          <!-- Empty state -->
          <p *ngIf="openResourceCount === 0" class="mt-3 text-center text-xs text-slate-400">No open findings to track.</p>

        </div>
      </div>

    </div>

    <!-- ══ SECTION 3 + 5: Service Health + Trends ═════════════════════════ -->
    <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">

      <!-- Service Health -->
      <div class="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div class="border-b border-slate-100 px-5 py-4">
          <h2 class="text-sm font-semibold text-slate-900">Service Health</h2>
          <p class="text-xs text-slate-400">Open findings by monitored service type</p>
        </div>
        <div class="divide-y divide-slate-50">
          <div *ngFor="let row of serviceHealth" class="flex items-center gap-4 px-5 py-4">
            <div class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100">
              <lucide-icon [name]="row.icon" [size]="17" class="text-slate-500" [strokeWidth]="1.75"></lucide-icon>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-sm font-medium text-slate-900">{{ row.label }}</p>
              <p class="text-xs text-slate-400">{{ row.total }} open finding{{ row.total !== 1 ? 's' : '' }}</p>
            </div>
            <div class="flex items-center gap-2">
              <span *ngIf="row.critical > 0" class="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                {{ row.critical }} critical
              </span>
              <span
                class="rounded-full px-2.5 py-0.5 text-xs font-medium"
                [ngClass]="row.statusClass"
              >
                {{ row.total === 0 ? 'Healthy' : row.critical > 0 ? 'Critical' : 'Warning' }}
              </span>
              <a [routerLink]="row.route" class="text-slate-400 hover:text-violet-600 transition-colors">
                <lucide-icon name="arrow-right" [size]="15" [strokeWidth]="2"></lucide-icon>
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Trends: Findings last 7 days -->
      <div class="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div class="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <div>
            <h2 class="text-sm font-semibold text-slate-900">Findings Trend</h2>
            <p class="text-xs text-slate-400">New findings per day, last 7 days</p>
          </div>
          <lucide-icon name="trending-up" [size]="16" class="text-slate-300" [strokeWidth]="1.75"></lucide-icon>
        </div>
        <div class="p-5">
          <div class="flex items-end gap-2" style="height: 96px;">
            <div *ngFor="let bar of trendBars" class="flex flex-1 flex-col items-center justify-end gap-1">
              <span class="text-xs text-slate-400 leading-none" style="min-height: 14px;">
                {{ bar.count > 0 ? bar.count : '' }}
              </span>
              <div
                class="w-full rounded-t-sm transition-all"
                [class.bg-violet-400]="bar.count > 0"
                [class.bg-slate-100]="bar.count === 0"
                [style.height.px]="bar.height > 0 ? bar.height : 3"
              ></div>
            </div>
          </div>
          <div class="mt-2 flex gap-2">
            <div *ngFor="let bar of trendBars" class="flex flex-1 justify-center">
              <span class="text-xs text-slate-400">{{ bar.label }}</span>
            </div>
          </div>
          <p *ngIf="maxTrendCount === 0" class="mt-3 text-center text-xs text-slate-400">No findings in this period.</p>
        </div>
      </div>

    </div>

    <!-- ══ SECTION 6 + 7: Stuck / Friction + Recent Activity ═════════════ -->
    <div class="grid grid-cols-1 gap-6 lg:grid-cols-3">

      <!-- Stuck / Friction (2/3) -->
      <div class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm lg:col-span-2">
        <div class="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <div>
            <h2 class="text-sm font-semibold text-slate-900">Needs Attention</h2>
            <p class="text-xs text-slate-400">Open findings unresolved for more than 24 hours</p>
          </div>
          <span *ngIf="stuckFindings.length > 0" class="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700">
            {{ stuckFindings.length }} stale
          </span>
        </div>

        <!-- Empty state -->
        <div *ngIf="stuckFindings.length === 0" class="flex flex-col items-center justify-center py-10 text-center">
          <div class="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50">
            <lucide-icon name="circle-check" [size]="20" class="text-emerald-500" [strokeWidth]="1.75"></lucide-icon>
          </div>
          <p class="mt-2.5 text-sm font-medium text-slate-700">No stale findings</p>
          <p class="mt-0.5 text-xs text-slate-400">All open findings are less than 24 hours old.</p>
        </div>

        <!-- Stuck findings table -->
        <div *ngIf="stuckFindings.length > 0" class="overflow-x-auto">
          <table class="w-full">
            <thead>
              <tr class="border-b border-slate-100">
                <th class="px-5 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-400">Severity</th>
                <th class="px-5 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-400">Finding</th>
                <th class="px-5 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-400">Service</th>
                <th class="px-5 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-400">Open for</th>
                <th class="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-50">
              <tr *ngFor="let f of stuckFindings" class="transition-colors hover:bg-slate-50">
                <td class="px-5 py-3.5">
                  <span
                    class="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium"
                    [ngClass]="severityBadgeClass(f.severity)"
                  >
                    <span class="h-1.5 w-1.5 rounded-full" [ngClass]="severityDotClass(f.severity)"></span>
                    {{ f.severity | titlecase }}
                  </span>
                </td>
                <td class="max-w-xs px-5 py-3.5">
                  <p class="truncate text-sm font-medium text-slate-900">{{ f.title }}</p>
                  <p class="truncate text-xs text-slate-400">{{ f.resourceId }}</p>
                </td>
                <td class="px-5 py-3.5 text-xs text-slate-500">{{ serviceTypeLabel(f.serviceType) }}</td>
                <td class="px-5 py-3.5">
                  <span class="rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700">
                    {{ formatAge(f.observedAt) }}
                  </span>
                </td>
                <td class="px-5 py-3.5">
                  <a
                    [routerLink]="serviceRoute(f.serviceType)"
                    class="flex items-center gap-0.5 text-xs font-medium text-violet-600 hover:text-violet-800"
                  >
                    View
                    <lucide-icon name="arrow-right" [size]="12" [strokeWidth]="2.5"></lucide-icon>
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Recent Activity (1/3) -->
      <div class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div class="border-b border-slate-100 px-5 py-4">
          <h2 class="text-sm font-semibold text-slate-900">Recent Activity</h2>
          <p class="text-xs text-slate-400">Findings opened and resolved</p>
        </div>

        <!-- Empty state -->
        <div *ngIf="recentActivity.length === 0" class="flex flex-col items-center justify-center py-10 text-center">
          <lucide-icon name="inbox" [size]="22" class="text-slate-300" [strokeWidth]="1.5"></lucide-icon>
          <p class="mt-2 text-xs text-slate-400">No recent activity.</p>
        </div>

        <!-- Activity list -->
        <div *ngIf="recentActivity.length > 0" class="divide-y divide-slate-50">
          <div *ngFor="let event of recentActivity" class="flex gap-3 px-5 py-3.5">
            <div
              class="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full"
              [ngClass]="event.iconBg"
            >
              <lucide-icon [name]="event.icon" [size]="13" [ngClass]="event.iconColor" [strokeWidth]="2"></lucide-icon>
            </div>
            <div class="min-w-0 flex-1">
              <p class="truncate text-xs leading-relaxed text-slate-700">{{ event.message }}</p>
              <p class="mt-0.5 text-xs text-slate-400">{{ event.relativeTime }}</p>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- ══ SECTION 8: Quota ════════════════════════════════════════════════ -->
    <div *ngIf="quota" class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div class="flex items-center gap-5 px-5 py-4">
        <div class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100">
          <lucide-icon name="activity" [size]="17" class="text-slate-500" [strokeWidth]="1.75"></lucide-icon>
        </div>
        <div class="flex-1 min-w-0">
          <div class="mb-1 flex items-center justify-between gap-4">
            <p class="text-sm font-medium text-slate-900">Monitoring Quota</p>
            <p class="text-xs text-slate-500 whitespace-nowrap">
              {{ quota.currentCount }} / {{ quota.limit }} resources
              <span class="ml-1 text-slate-400">({{ quota.tier }})</span>
            </p>
          </div>
          <div class="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
            <div
              class="h-full rounded-full transition-all duration-500"
              [ngClass]="quotaBarClass"
              [style.width.%]="quotaPercent"
            ></div>
          </div>
        </div>
        <a routerLink="/settings/integrations" class="flex-shrink-0 text-xs font-medium text-violet-600 hover:text-violet-800">
          Manage
        </a>
      </div>
    </div>

  </div>
</div>
